import { html, unsafeCSS } from 'lit';
import { customElement } from 'lit/decorators.js';
import style from './credit-quality-table.scss?inline';

import { PopBaseComponent } from '../../base/pop-base-component.js';
import { Constants } from './credit-quality-table.constants.js';
import type { CreditQualityTableContentObject } from './credit-quality-table.model.js';
import {
  formatToNDecimalPlaces,
  formatNumberToShowSign,
} from '../../common/utils/formatNumber.js';

/**
 * @tag pop-credit-quality-table-component
 * @attribute { CreditQualityTableContentObject } [data-component-object] CreditQualityTable content object
 */
@customElement(Constants.TAG_NAME)
export class PopCreditQualityTableComponent extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }
  /**
   * @internal
   */
  tagName = Constants.TAG_NAME;

  /**
   * CreditQualityTable content object
   */
  componentObject?: CreditQualityTableContentObject;

  private renderTableHead() {
    return html`
      <tr>
        <th id="credit-rating-column" scope="col">
          <beacon-table-header
            variant="normal"
            sortable="true"
            sort-direction="descending"
          >
            ${Constants.CREDIT_RATING_COLUMN_HEADER}
          </beacon-table-header>
        </th>
        <th id="percent-portfolio-column" scope="col" class="right-align">
          <beacon-table-header variant="normal" sortable="true">
            ${Constants.PERCENT_OF_PORTFOLIO_COLUMN_HEADER}
          </beacon-table-header>
        </th>
        <th
          id="percent-change-from-last-period-column"
          scope="col"
          class="right-align"
        >
          <beacon-table-header variant="normal" sortable="true">
            ${Constants.PERCENT_CHANGE_FROM_LAST_PERIOD_COLUMN_HEADER}
          </beacon-table-header>
        </th>
        <th id="benchmark-column" scope="col" class="right-align">
          <beacon-table-header variant="normal" sortable="true">
            ${Constants.BENCHMARK_COLUMN_HEADER}
          </beacon-table-header>
        </th>
      </tr>
    `;
  }

  private renderTableBody() {
    const itemList = this.componentObject?.itemList;

    // Quality rating is sorted by rank number, which is turned to negative values to match
    // with how the column is sorted by beacon sort function. i.e lower rank number value is better rank
    return html`
      ${itemList?.map((item) => {
        return html`
          <tr>
            <th
              data-beacon-sort-type="number"
              data-beacon-sort-value="${-1 * item.rank}"
            >
              <beacon-table-stub>
                <span
                  class="b-typography-sans-serif--body--100 b-font-weight-bold"
                >
                  ${item.qualityRating || Constants.EMPTY_CELL_VALUE}
                </span>
              </beacon-table-stub>
            </th>
            <td>
              <beacon-table-record
                variant="numeral"
                sort-value="${formatToNDecimalPlaces(item.percentOfFund, 2)}"
              >
                ${formatToNDecimalPlaces(item.percentOfFund, 2)}
              </beacon-table-record>
            </td>
            <td>
              <beacon-table-record
                variant="numeral"
                sort-value="${formatNumberToShowSign(
                  item.percentOfChangeFromLastPeriod,
                )}"
              >
                ${formatNumberToShowSign(item.percentOfChangeFromLastPeriod)}
              </beacon-table-record>
            </td>
            <td>
              <beacon-table-record
                variant="numeral"
                sort-value="${formatToNDecimalPlaces(
                  item.percentOfIndicativeBenchmark,
                  2,
                )}"
              >
                ${formatToNDecimalPlaces(item.percentOfIndicativeBenchmark, 2)}
              </beacon-table-record>
            </td>
          </tr>
        `;
      })}
    `;
  }

  render() {
    return this.renderComponent(html`
      <section class="credit-quality-table-content">
        <beacon-table variant="fixed" sortable="true">
          <table>
            <thead>
              ${this.renderTableHead()}
            </thead>
            <tbody>
              ${this.renderTableBody()}
            </tbody>
          </table>
        </beacon-table>
      </section>
    `);
  }
}
